import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPermissions } from '../utils/permissions';
import { subscribeToCustomersPendingCount, subscribeToTerraFlowProjectsPendingCount, subscribeToTerraFlowProjectsChangeCount } from '../utils/storage';
import { NotificationBadge } from './templates';
import AccessDeniedModal from './AccessDeniedModal';
import tiLogoMark from '../assets/TI-logo-no-text.png';
import ThemeToggle from './ThemeToggle';
import './PortalSelector.css';

export default function PortalSelector({ userProfile }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showTerraFlowDenied, setShowTerraFlowDenied] = useState(false);
  const profileMenuRef = useRef(null);

  const isAdmin = userProfile?.roles?.includes('admin');
  const perms = getPermissions(userProfile?.roles || []);
  const [pendingCustomers, setPendingCustomers] = useState(0);
  const [pendingProjects, setPendingProjects] = useState(0);
  const [changedProjects, setChangedProjects] = useState(0);

  // Show the pending-assignment badge on the TerraFlow card for users who can
  // access it. The red badge sums both red notifications inside the manager
  // (unassigned customers + projects with no linked TIB object); a separate
  // orange badge mirrors the projects-changed count.
  useEffect(() => {
    if (!perms.canAccessTerraFlow) return undefined;
    return subscribeToCustomersPendingCount(setPendingCustomers);
  }, [perms.canAccessTerraFlow]);
  useEffect(() => {
    if (!perms.canAccessTerraFlow) return undefined;
    return subscribeToTerraFlowProjectsPendingCount(setPendingProjects);
  }, [perms.canAccessTerraFlow]);
  useEffect(() => {
    if (!perms.canAccessTerraFlow) return undefined;
    return subscribeToTerraFlowProjectsChangeCount(setChangedProjects);
  }, [perms.canAccessTerraFlow]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    }
    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  function handleTerraFlowClick() {
    if (perms.canAccessTerraFlow) {
      navigate('/terraflow');
    } else {
      setShowTerraFlowDenied(true);
    }
  }

  return (
    <div className="portal-page">
      {/* ── Left Panel: Branding ── */}
      <div className="portal-left">
        <div className="portal-branding">
          <img src={tiLogoMark} alt="Terra Inspectioneering" className="portal-logo" />
          <h1 className="portal-title">Terra Inspectioneering Base</h1>
        </div>
      </div>

      {/* ── Right Panel: Portal Cards ── */}
      <div className="portal-right">
        {/* User profile bar */}
        <div className="portal-user-bar">
          <ThemeToggle />
          <div className="portal-profile-wrapper" ref={profileMenuRef}>
            <button
              className="portal-profile-toggle"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              {user?.photoURL ? (
                <img className="portal-avatar" src={user.photoURL} alt="" referrerPolicy="no-referrer" />
              ) : (
                <div className="portal-avatar-placeholder">
                  {(user?.displayName || user?.email || '?')[0].toUpperCase()}
                </div>
              )}
              <span className="portal-user-name">{user?.displayName || user?.email}</span>
              <span className={`portal-dropdown-arrow ${showProfileMenu ? 'open' : ''}`}>&#9662;</span>
            </button>
            {showProfileMenu && (
              <div className="portal-profile-dropdown">
                <div className="portal-profile-info">
                  <p className="portal-profile-name">{user?.displayName}</p>
                  <p className="portal-profile-email">{user?.email}</p>
                </div>
                <div className="portal-profile-divider" />
                <button className="portal-profile-item signout" onClick={logout}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="portal-cards-wrapper">
          <h2 className="portal-cards-heading">Select a portal</h2>

          <div className="portal-cards">
            {/* TIRP Card — Report/page icon */}
            <div className="portal-card" onClick={() => navigate('/dashboard')}>
              <div className="portal-card-icon">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <line x1="10" y1="9" x2="8" y2="9" />
                </svg>
              </div>
              <div className="portal-card-content">
                <h3 className="portal-card-title">Reporting Portal</h3>
                <p className="portal-card-abbr">TIRP</p>
                <p className="portal-card-desc">Manage test sites, inspections, equipment and generate reports.</p>
              </div>
              <span className="portal-card-arrow">&rsaquo;</span>
            </div>

            {/* TerraFlow Manager Card — node/flow icon */}
            <div className="portal-card" onClick={handleTerraFlowClick}>
              <NotificationBadge count={pendingCustomers + pendingProjects} className="notif-badge-corner" title={`${pendingCustomers} customer(s) need assignment, ${pendingProjects} project(s) without a TIB object`} />
              <NotificationBadge count={changedProjects} className="notif-badge-corner-2 notif-badge-warning" title={`${changedProjects} project(s) updated by TerraFlow`} />
              <div className="portal-card-icon">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="5" cy="6" r="2.5" />
                  <circle cx="5" cy="18" r="2.5" />
                  <circle cx="19" cy="12" r="2.5" />
                  <path d="M7.5 6H13a3.5 3.5 0 0 1 3.5 3.5v.5" />
                  <path d="M7.5 18H13a3.5 3.5 0 0 0 3.5-3.5v-.5" />
                </svg>
              </div>
              <div className="portal-card-content">
                <h3 className="portal-card-title">TerraFlow Manager</h3>
                <p className="portal-card-abbr">TerraFlow</p>
                <p className="portal-card-desc">View synced TerraFlow projects and turn them into TIB objects and inspections.</p>
              </div>
              <span className="portal-card-arrow">&rsaquo;</span>
            </div>

            {/* Mergepoint Card — stacked documents icon */}
            <div className="portal-card" onClick={() => navigate('/mergepoint')}>
              <div className="portal-card-icon">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 3h6l4 4v9a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                  <polyline points="15 3 15 7 19 7" />
                  <path d="M5 7v12a2 2 0 0 0 2 2h9" />
                </svg>
              </div>
              <div className="portal-card-content">
                <h3 className="portal-card-title">Mergepoint</h3>
                <p className="portal-card-abbr">Mergepoint</p>
                <p className="portal-card-desc">Re-assemble hand-edited report chapters into one page-numbered PDF with a rebuilt table of contents.</p>
              </div>
              <span className="portal-card-arrow">&rsaquo;</span>
            </div>
          </div>
        </div>

        {/* Admin button — only visible to admins */}
        {isAdmin && (
          <div className="portal-right-bottom">
            <button className="portal-admin-btn" onClick={() => navigate('/admin')}>
              I am the Admin
            </button>
          </div>
        )}
      </div>

      {/* Access denied modal for TerraFlow Manager */}
      {showTerraFlowDenied && (
        <AccessDeniedModal
          roles={userProfile?.roles || []}
          onClose={() => setShowTerraFlowDenied(false)}
        />
      )}
    </div>
  );
}
